/**
 * Action wiring — the "when I click here, those update" part of the dashboard.
 *
 *   source widget → ActionSignal → (its configured targets) → extra constraint
 *
 * Signals are held as DATA, never as a pre-built SQL string, because the same
 * selection has to become three different things: an ArcGIS `where`, a
 * GeoServer `CQL_FILTER`, and a readable chip in the UI.
 *
 * Only widgets a source explicitly targets are affected. That is deliberate: a
 * dashboard where every click silently re-filters everything is impossible to
 * reason about, and it is not what Esri does either.
 */

import type {
  ActionKind,
  ActionMatch,
  ActionSignal,
  DashboardField,
  DashboardWidget,
} from '../types/dashboardTypes';

/** Live signals, keyed by the widget that raised them. */
export type ActionState = Record<string, ActionSignal>;

/**
 * The constraint reaching one target widget.
 *
 * `where` and `extent` are separate because they travel differently: an
 * attribute clause goes in the query's `where`, an envelope goes in the
 * service's spatial parameters.
 */
export interface IncomingConstraint {
  where: string;
  extent?: [number, number, number, number];
  /**
   * A shape the target must fall inside, from a spatially-matched action.
   *
   * Separate from `where` for the same reason `extent` is: an attribute clause
   * goes in the query's `where`, a shape goes in the service's spatial
   * parameters. They are two different sentences to two different parts of the
   * request, and merging them would mean building SQL that has to be taken
   * apart again.
   */
  geometry?: GeoJSON.Geometry;
  /** Human-readable, for the chip shown on the affected widget. */
  labels: string[];
  /** Sources to clear when the user dismisses the chip. */
  sourceIds: string[];
}

const EMPTY: IncomingConstraint = { where: '', labels: [], sourceIds: [] };

/** Quotes a selection value according to the target field's type. */
function literal(value: string, fields: DashboardField[], fieldName: string): string {
  const field = fields.find((entry) => entry.name.toLowerCase() === fieldName.toLowerCase());
  if (field?.type === 'number' && value !== '' && Number.isFinite(Number(value))) {
    return value;
  }
  // Doubling is the SQL and CQL escape for a quote in a literal.
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * Signals that should reach `widget`, from every source targeting it.
 *
 * A widget never receives its own signal: clicking a slice must not remove
 * every other slice from the chart you just clicked, which is the classic
 * self-filter bug.
 *
 * Kept separate from `buildConstraint` because only the widget itself knows its
 * field types, which is what decides whether a value is quoted.
 */
export function signalsFor(
  widget: DashboardWidget,
  widgets: DashboardWidget[],
  state: ActionState,
  /** Which action kinds to collect. Defaults to filtering. */
  kinds: ActionKind[] = ['filter'],
): ActionSignal[] {
  const signals: ActionSignal[] = [];

  for (const source of widgets) {
    if (source.id === widget.id) continue;

    const signal = state[source.id];
    if (!signal) continue;

    /* The MATCH mode belongs to the action, not to the signal — one selection
       can reach one widget by attribute and another by location. It is stamped
       on the copy handed to this target so the target does not have to look
       the action up again. */
    const reaching = (source.actions ?? []).filter(
      (action) => kinds.includes(action.kind) && action.targetWidgetIds.includes(widget.id),
    );
    if (reaching.length === 0) continue;

    if (signal.kind !== 'selection') {
      // An extent is already a shape, so it carries neither a match nor a pair.
      signals.push(signal);
      continue;
    }

    const spatial = reaching.some((action) => action.match === 'spatial');
    /* The field pairing belongs to the action too, and to THIS target within
       it — the same source can hand one widget a ward code and another a zone
       name. Read here so the target does not have to look the action up. */
    const pair = reaching.find((action) => action.fieldMap?.[widget.id])?.fieldMap?.[widget.id];

    signals.push({
      ...signal,
      ...(spatial ? { match: 'spatial' as ActionMatch } : null),
      ...(pair && !spatial ? { pair } : null),
    });
  }

  return signals;
}

/** True when any source drives `widget` with the given kind. */
export function hasKindTargeting(
  widget: DashboardWidget,
  widgets: DashboardWidget[],
  state: ActionState,
  kind: ActionKind,
): boolean {
  return signalsFor(widget, widgets, state, [kind]).length > 0;
}

/** Turns the signals reaching a widget into a query constraint plus a label. */
export function buildConstraint(
  signals: ActionSignal[],
  /** Fields of the TARGET's data source, so literals are typed correctly. */
  fields: DashboardField[],
): IncomingConstraint {
  const clauses: string[] = [];
  const labels: string[] = [];
  const sourceIds: string[] = [];
  let extent: IncomingConstraint['extent'];
  let geometry: IncomingConstraint['geometry'];

  for (const signal of signals) {
    if (signal.kind === 'selection') {
      /* Matched by location: the shape replaces the clause entirely. Sending
         both would AND them, and a ward's polygon ANDed with a ward CODE the
         pipe layer does not carry matches nothing. */
      if (signal.match === 'spatial') {
        if (!signal.geometry) continue;
        geometry = signal.geometry;
        const spatialLabel =
          signal.values && signal.values.length > 1
            ? `${signal.values.length} selected`
            : signal.value || 'the selection';
        labels.push(`${signal.sourceTitle}: inside ${spatialLabel}`);
        sourceIds.push(signal.sourceId);
        continue;
      }
      /* Which column on each side. Unset means both sides use the field the
         source broadcasts, which is how every action behaved before pairing
         existed — so an old dashboard keeps working untouched. */
      const sourceField = signal.pair?.source || signal.field;
      const targetField = signal.pair?.target || sourceField;
      if (!targetField) continue;

      /* When the target compares against a column the source does NOT display,
         the values were looked up at the source and travel on the signal. An
         empty list means the lookup found nothing; `undefined` means it has
         not come back yet. Neither should filter — dropping the signal leaves
         the target unconstrained rather than briefly empty. */
      const values =
        sourceField === signal.field
          ? signal.values && signal.values.length > 0
            ? signal.values
            : [signal.value]
          : signal.fieldValues?.[sourceField];
      if (!values || values.length === 0) continue;

      const isBlank = (value: string) => value === '' || value === '(blank)';
      const blanks = values.filter(isBlank);
      const present = values.filter((value) => !isBlank(value));

      const parts: string[] = [];
      if (present.length === 1) {
        parts.push(`${targetField} = ${literal(present[0], fields, targetField)}`);
      } else if (present.length > 1) {
        // One value can map to several — a ward name covering two codes.
        const list = present.map((value) => literal(value, fields, targetField)).join(', ');
        parts.push(`${targetField} IN (${list})`);
      }
      if (blanks.length > 0) parts.push(`${targetField} IS NULL`);
      if (parts.length === 0) continue;

      clauses.push(parts.length === 1 ? parts[0] : `(${parts.join(' OR ')})`);

      /* The label names the mapped column when it differs, so a board whose
         numbers look wrong can be traced back to the pairing that produced
         them without opening the config. */
      const shown =
        signal.values && signal.values.length > 1
          ? `${signal.values.length} selected`
          : signal.value || '(blank)';
      labels.push(
        targetField === signal.field
          ? `${signal.sourceTitle}: ${shown}`
          : `${signal.sourceTitle}: ${shown} (${sourceField} → ${targetField})`,
      );
      sourceIds.push(signal.sourceId);
    } else {
      // Several map extents cannot be intersected meaningfully, so the last
      // one wins — matching how a dashboard is actually built, with one map
      // driving the page.
      extent = signal.extent;
      labels.push(`${signal.sourceTitle}: map extent`);
      sourceIds.push(signal.sourceId);
    }
  }

  if (clauses.length === 0 && !extent && !geometry) return EMPTY;

  return {
    where: clauses.length > 1 ? clauses.map((clause) => `(${clause})`).join(' AND ') : (clauses[0] ?? ''),
    extent,
    geometry,
    labels,
    sourceIds,
  };
}

/** ANDs a widget's own filter expression with the one actions contributed. */
export function combineWhere(own: string, incoming: string): string {
  const parts = [own?.trim(), incoming?.trim()].filter(Boolean) as string[];
  if (parts.length === 0) return '';
  return parts.length === 1 ? parts[0] : parts.map((part) => `(${part})`).join(' AND ');
}

/** True when this widget drives at least one other widget. */
export function hasActiveTargets(widget: DashboardWidget): boolean {
  return (widget.actions ?? []).some((action) => action.targetWidgetIds.length > 0);
}
